import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

interface SetBackgroundDialogProps {
  open: boolean;
  initialRepeat: boolean;
  onClose: () => void;
  onSave: (file: File, repeat: boolean) => Promise<boolean>;
}

const SetBackgroundDialog = ({
  open,
  initialRepeat,
  onClose,
  onSave,
}: SetBackgroundDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [backgroundMode, setBackgroundMode] = useState<'fullscreen' | 'repeating'>('fullscreen');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedFile(null);
    setBackgroundMode(initialRepeat ? 'repeating' : 'fullscreen');
    setIsSaving(false);
  }, [initialRepeat, open]);

  const selectedFileLabel = useMemo(() => {
    if (!selectedFile) {
      return 'No image selected';
    }

    return selectedFile.name;
  }, [selectedFile]);

  const handleSelectImage = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (!selectedFile || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      const didSave = await onSave(selectedFile, backgroundMode === 'repeating');
      if (didSave) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Set Background</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select a new background
            </Typography>
            <RadioGroup
              row
              value={backgroundMode}
              onChange={(event) =>
                setBackgroundMode(event.target.value as 'fullscreen' | 'repeating')
              }
            >
              <FormControlLabel value="fullscreen" control={<Radio />} label="Fullscreen" />
              <FormControlLabel value="repeating" control={<Radio />} label="Repeating" />
            </RadioGroup>
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              onClick={handleSelectImage}
              disabled={isSaving}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Select Image
            </Button>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 40,
                px: 1.5,
                py: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.default',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                color={selectedFile ? 'text.primary' : 'text.secondary'}
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedFileLabel}
              </Typography>
            </Box>
          </Stack>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const [file] = Array.from(event.target.files ?? []);
              setSelectedFile(file ?? null);
              event.target.value = '';
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!selectedFile || isSaving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetBackgroundDialog;
